import {
	InterestedParty,
	LiveInformationProvider,
	SuccessfulCacheItem
} from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import {
	KnowledgeItemInterests,
	list,
	listMore,
	LiveInformationKnowledgeItemRequestModel,
	LiveInformationKnowledgeItemResponseModel,
	LiveInformationKnowledgeItemResponseModelData
} from "client/Api/Workspace/LiveInformation/Hub/knowledgeItemLiveInformation";
import {
	HubItemCategoryIdentifierModel
} from "client/Api/Hub/common";
import { Guid } from "client_legacy/ndaw/guid";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	KnowledgeItemPagingInformationModel,
	KnowledgeItemPreviewItemModel
} from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { ensureNotNil } from "client/Misc/utils";
import { HubTileCategoryIds } from "client/Features/Workspace/Tiles/Hub/common";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";
import { never } from "client_legacy/ndaw/errors";
import { HubItemTileResourceId, HubItemTileResourceKind } from "client/Api/Workspace/workspace";
import { listCategories } from "client/Api/Workspace/LiveInformation/Hub/hubItemCategoriesLiveInformation";

interface InterestedKnowledgeItemParty extends InterestedParty<Guid, HubItemTileResourceId> {
	updateItems(page: LazyLoadPageResult<KnowledgeItemPreviewItemModel>): void;
	updateLastPinnedItem(lastPinnedItem: KnowledgeItemPreviewItemModel | null): void;
}

export type KnowledgeItemLiveInformationProviderOptions = {
	intervalInSeconds: number;
};

export class KnowledgeItemLiveInformationProvider extends LiveInformationProvider<
	KnowledgeItemLiveInformationProviderOptions,
	Guid,
	HubItemTileResourceId,
	KnowledgeItemInterests,
	InterestedKnowledgeItemParty,
	LiveInformationKnowledgeItemResponseModelData
>
{
	private _listRetrievalPromise: Promise<readonly HubItemCategoryIdentifierModel[]> | undefined;

	public get itemKind(): HubItemKind {
		return HubItemKind.KnowledgeItem;
	}

	constructor(options: KnowledgeItemLiveInformationProviderOptions) {
		super(options);
	}

	protected async getDataFromBackend(
		entries: ReadonlyArray<[HubItemTileResourceId, KnowledgeItemInterests]>
	): Promise<ReadonlyArray<[HubItemTileResourceId, LiveInformationKnowledgeItemResponseModelData]>> {
		const requestModels: LiveInformationKnowledgeItemRequestModel[] = entries.map(x => {
			const [resource, interests] = x;

			return {
				resource,
				interests
			};
		});

		const responses: readonly LiveInformationKnowledgeItemResponseModel[] = await list(requestModels);

		return responses.map(x => [x.resource, x.data]);
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<KnowledgeItemInterests, LiveInformationKnowledgeItemResponseModelData>,
		response: [KnowledgeItemInterests, LiveInformationKnowledgeItemResponseModelData]
	): void {
		const [retrievedInterests, data] = response;

		if (retrievedInterests.items) {
			cacheItem.retrievedInterests.items = true;
			cacheItem.data.items = data.items;
		}

		if (retrievedInterests.lastPinnedItem) {
			cacheItem.retrievedInterests.lastPinnedItem = true;
			cacheItem.data.lastPinnedItem = data.lastPinnedItem;
		}
	}

	protected informParty(
		party: InterestedKnowledgeItemParty,
		cacheItem: [KnowledgeItemInterests, LiveInformationKnowledgeItemResponseModelData]
	): void {
		const [retrievedInterests, data] = cacheItem;
		const interestedIn: KnowledgeItemInterests = this.getInterestsForEnsuredParty(party);

		if (interestedIn.items && retrievedInterests.items) {
			party.updateItems(
				ensureNotNil(data.items),
			);
		}

		if (interestedIn.lastPinnedItem && retrievedInterests.lastPinnedItem) {
			party.updateLastPinnedItem(data.lastPinnedItem);
		}
	}

	protected override createResourceMap<TValue>(): Map<HubItemTileResourceId, TValue> {
		return new NormalizedMap<HubItemTileResourceId, TValue>(
			normalizeKey
		);
	}

	public reload(party: InterestedKnowledgeItemParty) {
		this.clearCacheForResource(party.resourceId);
		const interests: KnowledgeItemInterests = this.getInterestsForEnsuredParty(party);
		this.queuePartyForDataRetrieval(party, interests);
	}

	public listCategories(): Promise<readonly HubItemCategoryIdentifierModel[]> {
		if (!this._listRetrievalPromise) {
			this._listRetrievalPromise = listCategories(HubItemKind.KnowledgeItem);
		}
		return this._listRetrievalPromise;
	}

	public loadMoreItems(
		categoryIds: HubTileCategoryIds,
		_: Required<KnowledgeItemInterests>,
		lastRetrievedItem: KnowledgeItemPreviewItemModel,
		signal?: AbortSignal
	): Promise<LazyLoadPageResult<KnowledgeItemPreviewItemModel>>
	{
		const paginationInfo: KnowledgeItemPagingInformationModel = {
			id: lastRetrievedItem.id,
			publicationDate: lastRetrievedItem.publicationDate,
			title: lastRetrievedItem.title,
			viewsCount: lastRetrievedItem.viewsCount,
			pinDate: lastRetrievedItem.pinDate,
			relevanceScore: lastRetrievedItem.relevanceScore
		};

		return listMore(
			{
				categoryIds,
				lastRetrievedItem: paginationInfo
			},
			signal
		);
	}
}

function normalizeKey(key: HubItemTileResourceId): string {
	switch (key.kind) {
		case HubItemTileResourceKind.CategoryIds:
			if (key.categoryIds === null) {
				return "categories|all";
			} else {
				return `categories|${key.categoryIds.join()}`;
			}
		case HubItemTileResourceKind.ItemIds:
			return `items|${key.itemIds.join()}`;
		default:
			never(key);
	}
}
